import * as THREE from 'three'
import { Controls } from 'three'
import type { NativeControlServices } from '@/plugins/control/NativeControlUtils'
import type { CharacterSnapshot, CharacterViewBinding } from '@/shared/types/character'
import { CameraMode } from '@/shared/types/core'
import type {
  CharacterService,
  PointerLockService,
  ViewEventRouter,
  ViewInputConsumer,
  ViewKeyEvent,
  ViewPointerEvent,
} from '@/shared/types/services'
import {
  isCharacterInputKey,
  JUMP_CODES,
  MOVE_BACKWARD_CODES,
  MOVE_FORWARD_CODES,
  MOVE_LEFT_CODES,
  MOVE_RIGHT_CODES,
  RUN_CODES,
} from './characterControlUtils'

export interface CharacterFirstPersonControllerOptions {
  services: NativeControlServices
  characterService: CharacterService
  viewEventRouter: ViewEventRouter
  pointerLockService: PointerLockService
  onVisualInput?: (reason: string) => void
}

const CHARACTER_FIRST_PERSON_INPUT_PRIORITY = 1_000

/** Character-first-person control mode driven by view-local input. */
export class CharacterFirstPersonController extends Controls<THREE.Camera> {
  private readonly services: NativeControlServices
  private readonly characterService: CharacterService
  private readonly viewEventRouter: ViewEventRouter
  private readonly pointerLockService: PointerLockService
  private readonly onVisualInput?: (reason: string) => void
  private readonly viewId: string
  private readonly keyState = new Set<string>()
  private readonly pendingLookDelta = new THREE.Vector2()
  private readonly consumer: ViewInputConsumer
  private readonly externalMoveInput = { forward: 0, right: 0 }

  private activeCharacterId: string | null = null
  private activePointerId: number | null = null
  private lastPointerScreen: THREE.Vector2 | null = null
  private lookYaw = 0
  private lookPitch = 0
  private jumpQueued = false
  private connected = false

  constructor(options: CharacterFirstPersonControllerOptions) {
    super(new THREE.PerspectiveCamera(), null)

    if (!options.services.viewId) {
      throw new Error('CharacterFirstPersonController requires a bound viewId')
    }

    this.services = options.services
    this.characterService = options.characterService
    this.viewEventRouter = options.viewEventRouter
    this.pointerLockService = options.pointerLockService
    this.onVisualInput = options.onVisualInput
    this.viewId = options.services.viewId
    this.enabled = false
    this.consumer = {
      name: `character-first-person:${this.viewId}`,
      priority: CHARACTER_FIRST_PERSON_INPUT_PRIORITY,
      onPointerDown: (event) => this.handlePointerDown(event),
      onPointerMove: (event) => this.handlePointerMove(event),
      onPointerUp: (event) => this.handlePointerUp(event),
      onWinKeyDown: (event) => this.handleKeyDown(event),
      onWinKeyUp: (event) => this.handleKeyUp(event),
    }
  }

  override connect(element: HTMLElement): void {
    if (this.services.rig.mode !== CameraMode.Perspective) {
      const state = this.services.rig.getState()
      this.services.rig.setState({
        ...state,
        mode: CameraMode.Perspective,
      })
    }

    super.connect(element)
    this.connected = true
    this.resetTransientInput()
    this.viewEventRouter.removeConsumer(this.consumer)
    this.viewEventRouter.addConsumer(this.consumer)
  }

  override disconnect(): void {
    if (!this.connected) {
      return
    }

    this.connected = false
    this.viewEventRouter.removeConsumer(this.consumer)
    this.exitPointerLockSession()
    this.resetTransientInput()
    this.releaseControlledCharacter()
    super.disconnect()
  }

  override dispose(): void {
    this.disconnect()
  }

  /**
   * Sets normalized external movement input, such as a virtual joystick.
   *
   * @param input - Forward/right movement axes in the range [-1, 1].
   */
  setExternalMoveInput(input: { forward?: number; right?: number } | null): void {
    this.externalMoveInput.forward = clampAxis(input?.forward ?? 0)
    this.externalMoveInput.right = clampAxis(input?.right ?? 0)
  }

  override update(_delta: number): void {
    if (!this.enabled) {
      return
    }

    const binding = this.characterService.getViewBinding(this.viewId)
    if (!binding) {
      this.exitPointerLockSession()
      this.releaseControlledCharacter()
      return
    }

    const handle = this.characterService.get(binding.characterId)
    if (!handle) {
      this.exitPointerLockSession()
      this.releaseControlledCharacter()
      return
    }

    if (binding.firstPerson.lookInteraction !== 'pointer-lock') {
      this.exitPointerLockSession()
    }

    if (this.activeCharacterId !== binding.characterId) {
      this.releaseControlledCharacter()
      this.activeCharacterId = binding.characterId
      this.syncLookFromSnapshot(handle.getSnapshot(), binding)
    }

    this.applyLookDelta(binding)
    this.characterService.setCommand(binding.characterId, {
      move: this.resolveMoveInput(),
      run: this.isRunPressed(),
      jump: this.consumeJump(),
      bodyYaw: this.lookYaw,
      aimYaw: this.lookYaw,
      aimPitch: this.lookPitch,
    })
  }

  private handlePointerDown(event: ViewPointerEvent): boolean {
    if (!this.enabled || event.viewId !== this.viewId) {
      return false
    }

    const binding = this.characterService.getViewBinding(this.viewId)
    if (!binding) {
      return false
    }

    this.domElement?.focus()

    if (binding.firstPerson.lookInteraction === 'pointer-lock') {
      if (event.button !== binding.firstPerson.pointerLockButton) {
        return false
      }
      event.domEvent.preventDefault()
      this.onVisualInput?.('character.input')
      return this.pointerLockService.request(this.viewId)
    }

    if (event.button !== binding.firstPerson.dragButton) {
      return false
    }

    this.activePointerId = event.domEvent.pointerId
    this.lastPointerScreen = event.screen.clone()
    event.domEvent.preventDefault()
    this.onVisualInput?.('character.input')
    return true
  }

  private handlePointerMove(event: ViewPointerEvent): boolean {
    if (
      !this.enabled ||
      event.viewId !== this.viewId ||
      this.activePointerId !== event.domEvent.pointerId ||
      !this.lastPointerScreen
    ) {
      return false
    }

    const delta = event.screen.clone().sub(this.lastPointerScreen)
    this.lastPointerScreen.copy(event.screen)
    this.pendingLookDelta.add(delta)
    event.domEvent.preventDefault()
    this.onVisualInput?.('character.input')
    return true
  }

  private handlePointerUp(event: ViewPointerEvent): boolean {
    if (
      !this.enabled ||
      event.viewId !== this.viewId ||
      this.activePointerId !== event.domEvent.pointerId
    ) {
      return false
    }

    this.activePointerId = null
    this.lastPointerScreen = null
    event.domEvent.preventDefault()
    this.onVisualInput?.('character.input')
    return true
  }

  private handleKeyDown(event: ViewKeyEvent): boolean {
    if (!this.enabled || event.viewId !== this.viewId || !isCharacterInputKey(event.code)) {
      return false
    }

    this.keyState.add(event.code)
    if (JUMP_CODES.includes(event.code) && !event.domEvent.repeat) {
      this.jumpQueued = true
    }
    event.domEvent.preventDefault()
    this.onVisualInput?.('character.input')
    return true
  }

  private handleKeyUp(event: ViewKeyEvent): boolean {
    if (!this.enabled || event.viewId !== this.viewId || !isCharacterInputKey(event.code)) {
      return false
    }

    this.keyState.delete(event.code)
    event.domEvent.preventDefault()
    this.onVisualInput?.('character.input')
    return true
  }

  private applyLookDelta(binding: CharacterViewBinding): void {
    const delta =
      binding.firstPerson.lookInteraction === 'pointer-lock'
        ? this.pointerLockService.consumeDelta(this.viewId)
        : this.consumeDragLookDelta()

    if (delta.lengthSq() <= 0) {
      return
    }

    const sensitivity = binding.firstPerson.lookSensitivity
    this.lookYaw -= delta.x * sensitivity
    this.lookPitch = THREE.MathUtils.clamp(
      this.lookPitch - delta.y * sensitivity,
      binding.firstPerson.minPitch,
      binding.firstPerson.maxPitch
    )
  }

  private consumeDragLookDelta(): THREE.Vector2 {
    const delta = this.pendingLookDelta.clone()
    this.pendingLookDelta.set(0, 0)
    return delta
  }

  private resolveMoveInput(): { forward: number; right: number } {
    const forward =
      this.resolveAxis(MOVE_FORWARD_CODES, MOVE_BACKWARD_CODES) + this.externalMoveInput.forward
    const right = this.resolveAxis(MOVE_RIGHT_CODES, MOVE_LEFT_CODES) + this.externalMoveInput.right

    return {
      forward: clampAxis(forward),
      right: clampAxis(right),
    }
  }

  private resolveAxis(positiveCodes: string[], negativeCodes: string[]): number {
    const positive = positiveCodes.some((code) => this.keyState.has(code))
    const negative = negativeCodes.some((code) => this.keyState.has(code))
    if (positive === negative) {
      return 0
    }
    return positive ? 1 : -1
  }

  private isRunPressed(): boolean {
    return RUN_CODES.some((code) => this.keyState.has(code))
  }

  private consumeJump(): boolean {
    const jump = this.jumpQueued
    this.jumpQueued = false
    return jump
  }

  private syncLookFromSnapshot(snapshot: CharacterSnapshot, binding: CharacterViewBinding): void {
    this.lookYaw = snapshot.aimYaw
    this.lookPitch = THREE.MathUtils.clamp(
      snapshot.aimPitch,
      binding.firstPerson.minPitch,
      binding.firstPerson.maxPitch
    )
  }

  private releaseControlledCharacter(): void {
    if (!this.activeCharacterId) {
      return
    }

    const characterId = this.activeCharacterId
    this.activeCharacterId = null
    const handle = this.characterService.get(characterId)
    if (!handle) {
      return
    }

    const command = handle.getCommand()
    this.characterService.setCommand(characterId, {
      move: {
        forward: 0,
        right: 0,
      },
      run: false,
      jump: false,
      bodyYaw: command.bodyYaw,
      aimYaw: command.aimYaw,
      aimPitch: command.aimPitch,
    })
  }

  private resetTransientInput(): void {
    this.keyState.clear()
    this.pendingLookDelta.set(0, 0)
    this.activePointerId = null
    this.lastPointerScreen = null
    this.jumpQueued = false
    this.setExternalMoveInput(null)
  }

  private exitPointerLockSession(): void {
    if (
      this.pointerLockService.isLockedFor(this.viewId) ||
      this.pointerLockService.pendingViewId === this.viewId
    ) {
      this.pointerLockService.exit()
    }
  }
}

function clampAxis(value: number): number {
  if (!Number.isFinite(value)) {
    return 0
  }
  return THREE.MathUtils.clamp(value, -1, 1)
}
